import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetInvestigationCategories } from "../../../../api/diagnosis/master/investigationCategoryAPI";
import { StorePathologyTest } from "../../../../api/diagnosis/master/pathologyTestAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

/**
 * Yup Validation Schema (matches Mongoose model)
 */
const schema = yup.object().shape({
	name: yup.string().required("Test name is required"),
	shortName: yup.string().required("Short name is required"),
	testType: yup.string().required("Test type is required"),
	investigationCategoryId: yup.string().required("Investigation Category is required"),
	unit: yup.string().required("Unit is required"),
	price: yup.number().typeError("Price must be a number").required("Price is required"),
	// intlPrice: yup.number().typeError("International price must be a number").required("International price is required"),
	colHeadingHide: yup.string().oneOf(["Yes", "No"]).required("Column heading hide is required"),
	worksheetPrint: yup.string().nullable(),
	tat: yup.string().nullable(),
	method: yup.string().nullable(),
	subCategory: yup.string().oneOf(["Yes", "No"]).required("Sub category is required"),
	rateEditable: yup.string().oneOf(["Yes", "No"]).required("Rate editable is required"),
	discountAllow: yup.string().oneOf(["Yes", "No"]).required("Discount allow is required"),
	reportDays: yup.number().nullable(),
	printSrNo: yup.number().nullable(),
	active: yup.string().oneOf(["Yes", "No"]).required("Active status is required"),
});


const AddPathologyTest = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [investigationCategories, setInvestigationCategories] = useState([]);

	const {
		register,
		handleSubmit,
		control,
		watch,
		formState: { errors }
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			active: "Yes",
			colHeadingHide: "No",
			subCategory: "No",
			rateEditable: "No",
			discountAllow: "No",
			normalValues: {
				type: "remark",
				values: {},
				ageRanges: [
					{
						fromAge: "",
						to: ""
					}
				],
				reactiveValues: {}
			}
		}
	});

	const normalType = watch("normalValues.type");

	const { fields: ageFields, append: appendAge, remove: removeAge } =
		useFieldArray({
			control,
			name: "normalValues.ageRanges"
		});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "cbcDetails"
	});

	const fetchInvestigationCategories = async () => {
		try {
			const res = await GetInvestigationCategories();
			if (res.success) setInvestigationCategories(res.data);
			else showToast("error", res.message);
		} catch (err) {
			showToast("error", err.message);
		}
	};

	useEffect(() => {
		fetchInvestigationCategories();
	}, []);


	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });
			const response = await StorePathologyTest(data);

			if (response.success) {
				showToast("success", response.message);
				navigate(
					`/diagnosis/master/pathology-test/list`
				);
			} else {
				showToast("error", response.message);
			}
		} catch (err) {
			showToast("error", err?.message || "Something went wrong");
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	const formRef = useRef(null);
	useKeyboardSubmit(handleSubmit, onSubmit);
	useEnterNavigation(formRef);

	const yesNoSelect = name => (
		<select className="form-control" {...register(name)}>
			<option value="Yes">Yes</option>
			<option value="No">No</option>
		</select>
	);

	const testTypes = [
		{ value: "Same", label: "Same" },
		{ value: "DLC", label: "DLC" },
		{ value: "Widal", label: "Widal" },
		{ value: "Culture", label: "Culture" },
		{ value: "Echo", label: "Echo" },
		{ value: "Bilirubin", label: "Bilirubin" },
		{ value: "S. Protein", label: "S. Protein" },
		{ value: "A/G Ratio", label: "A/G Ratio" },
		{ value: "Lipid Profile", label: "Lipid Profile" },
		{ value: "Stool", label: "Stool" },
		{ value: "Urine", label: "Urine" },
		{ value: "Semen", label: "Semen" },
		{ value: "C.B.C.", label: "C.B.C." },
		{ value: "PT(INR)", label: "PT(INR)" },
		{ value: "Renal", label: "Renal" },
		{ value: "HbAlc", label: "HbAlc" },
		{ value: "Prothrombin", label: "Prothrombin" },
		{ value: "IRON PROFILE", label: "IRON PROFILE" },
	];

	return (
		<Fragment>
			<Breadcrumb parent="Pathology Test" title="Add Pathology Test" />
			<div className="container-fluid">
				<div className="card">
					<div className="card-header d-flex justify-content-between">
						<h5>Add Pathology Test</h5>
						<Link to={`/diagnosis/master/pathology-test/list`}>
							<button className="btn btn-primary" id="list">
								List Pathology Tests
							</button>
							<UncontrolledTooltip target="list">
								List Pathology Tests
							</UncontrolledTooltip>
						</Link>
					</div>

					<form onSubmit={handleSubmit(onSubmit)} className="form theme-form">
						<div className="card-body">

							{/* BASIC INFO */}
							<h6>Basic Information</h6>
							<div className="row">
								<div className="col-md-6 mt-3">
									<label>Test Name *</label>
									<input className="form-control" {...register("name")} />
									{errors.name &&
										<p className="text-danger">
											{errors.name.message}
										</p>}
								</div>

								<div className="col-md-6 mt-3">
									<label>Short Name</label>
									<input className="form-control" {...register("shortName")} />
									{errors.shortName &&
										<p className="text-danger">
											{errors.shortName.message}
										</p>}
								</div>

								<div className="col-md-6 mt-3">
									<label>Test Type</label>
									<select className="form-control" {...register("testType")} >
										<option value="">Select Test Type</option>
										{testTypes.map(item => (
											<option key={item.value} value={item.value}>
												{item.label}
											</option>
										))}
									</select>
									{errors.testType &&
										<p className="text-danger">
											{errors.testType.message}
										</p>}
								</div>

								<div className="col-md-6 mt-3">
									<label>Investigation Category *</label>
									<select
										className="form-control"
										{...register("investigationCategoryId")}
									>
										<option value="">Select Investigation Category</option>
										{investigationCategories.map(item => (
											<option key={item._id} value={item._id}>
												{item.name}
											</option>
										))}
									</select>
									{errors.investigationCategoryId &&
										<p className="text-danger">
											{errors.investigationCategoryId.message}
										</p>}
								</div>
							</div>

							{/* PRICING */}
							<h6 className="mt-4">Pricing</h6>
							<div className="row">
								<div className="col-md-4">
									<label>Unit</label>
									<input type="number" className="form-control" {...register("unit")} />
									{errors.unit &&
										<p className="text-danger">
											{errors.unit.message}
										</p>}
								</div>
								<div className="col-md-4">
									<label>Price</label>
									<input type="number" className="form-control" {...register("price")} />
									{errors.price &&
										<p className="text-danger">
											{errors.price.message}
										</p>}
								</div>


								<div className="col-md-4">
									<label>Intl Price</label>
									<input type="number" className="form-control" {...register("intlPrice")} />
									{errors.intlPrice &&
										<p className="text-danger">
											{errors.intlPrice.message}
										</p>}
								</div>
							</div>

							{/* FLAGS */}
							<h6 className="mt-4">Settings</h6>
							<div className="row">
								<div className="col-md-3">
									<label>Column Heading Hide</label>
									{yesNoSelect("colHeadingHide")}
									{errors.colHeadingHide &&
										<p className="text-danger">
											{errors.colHeadingHide.message}
										</p>}
								</div>

								<div className="col-md-3">
									<label>Worksheet Print</label>
									<input type="text" className="form-control" {...register("worksheetPrint")} />
									{errors.worksheetPrint &&
										<p className="text-danger">
											{errors.worksheetPrint.message}
										</p>}
								</div>
								<div className="col-md-3">
									<label>TAT</label>
									<input type="text" className="form-control" {...register("tat")} />
									{errors.tat &&
										<p className="text-danger">
											{errors.tat.message}
										</p>}
								</div>
								<div className="col-md-3">
									<label>Method</label>
									<input type="text" className="form-control" {...register("method")} />
									{errors.method &&
										<p className="text-danger">
											{errors.method.message}
										</p>}
								</div>

								<div className="col-md-3">
									<label>Sub Category</label>
									{yesNoSelect("subCategory")}
									{errors.subCategory &&
										<p className="text-danger">
											{errors.subCategory.message}
										</p>}
								</div>

								<div className="col-md-3">
									<label>Rate Editable</label>
									{yesNoSelect("rateEditable")}
									{errors.rateEditable &&
										<p className="text-danger">
											{errors.rateEditable.message}
										</p>}
								</div>

								<div className="col-md-3">
									<label>Discount Allow</label>
									{yesNoSelect("discountAllow")}
									{errors.discountAllow &&
										<p className="text-danger">
											{errors.discountAllow.message}
										</p>}
								</div>

								<div className="col-md-3">
									<label>Report Days</label>
									<input type="number" className="form-control" {...register("reportDays")} />
									{errors.reportDays &&
										<p className="text-danger">
											{errors.reportDays.message}
										</p>}
								</div>
								<div className="col-md-3">
									<label>Print Sr No</label>
									<input type="number" className="form-control" {...register("printSrNo")} />
									{errors.printSrNo &&
										<p className="text-danger">
											{errors.printSrNo.message}
										</p>}
								</div>
							</div>

							{/* NORMAL VALUES SECTION */}
							<div className="card mt-4 p-3">
								<h6>Normal Values</h6>

								<div className="row">
									<div className="col-md-4">
										<label>Type</label>
										<select
											className="form-control"
											{...register("normalValues.type")}
										>
											<option value="range">&gt;= &lt;=</option>
											<option value="plusMinus">+ -</option>
											<option value="remark">Remark</option>
											<option value="age">Age</option>
											<option value="reactive">Reactive/Non-Reactive</option>
										</select>
										{errors?.normalValues?.type && (
											<p className="text-danger">
												{errors.normalValues.type.message}
											</p>
										)}
									</div>
								</div>

								{/* RANGE / +- / REMARK */}
								{["range", "plusMinus", "remark"].includes(normalType) && (
									<div className="row mt-3">
										{["male", "female", "child", "infant", "newBorn"].map(
											gender => (
												<div className="col-md-2" key={gender}>
													<label className="text-uppercase">{gender}</label>
													<input
														className="form-control"
														{...register(
															`normalValues.values.${gender}`
														)}
													/>
												</div>
											)
										)}
									</div>
								)}

								{/* REACTIVE */}
								{normalType === "reactive" && (
									<div className="row mt-3">
										{["male", "female", "child", "infant", "newBorn"].map(
											gender => (
												<div className="col-md-2" key={gender}>
													<label className="text-uppercase">{gender}</label>
													<select
														className="form-control"
														{...register(
															`normalValues.reactiveValues.${gender}`
														)}
													>
														<option value="Reactive">Reactive</option>
														<option value="Non-Reactive">
															Non-Reactive
														</option>
													</select>
												</div>
											)
										)}
									</div>
								)}

								{/* AGE TYPE */}
								{normalType === "age" && (
									<div className="mt-3">
										{ageFields.map((item, index) => (
											<div className="row mb-2" key={item.id}>
												<div className="col-md-3 mt-2">
													<div className="input-group">
														<input
															placeholder="From Age"
															type="number"
															className="form-control"
															{...register(
																`normalValues.ageRanges.${index}.fromAge`
															)}
														/>
														<select
															placeholder="Age Type"
															className="form-control"
															{...register(
																`normalValues.ageRanges.${index}.fromAgeType`
															)}
														>
															<option value="Days">Days</option>
															<option value="Months">Months</option>
															<option value="Years">Years</option>
														</select>
													</div>
												</div>

												<div className="col-md-3 mt-2">
													<div className="input-group">
														<input
															placeholder="To Age"
															type="number"
															className="form-control"
															{...register(
																`normalValues.ageRanges.${index}.toAge`
															)}
														/>
														<select
															placeholder="Age Type"
															className="form-control"
															{...register(
																`normalValues.ageRanges.${index}.toAgeType`
															)}
														>
															<option value="Days">Days</option>
															<option value="Months">Months</option>
															<option value="Years">Years</option>
														</select>
													</div>
												</div>

												<div className="col-md-3 mt-2">
													<input
														placeholder="Male From"
														type="number"
														className="form-control"
														{...register(
															`normalValues.ageRanges.${index}.maleFrom`
														)}
													/>
												</div>

												<div className="col-md-3 mt-2">
													<input
														placeholder="Male To"
														type="number"
														className="form-control"
														{...register(
															`normalValues.ageRanges.${index}.maleTo`
														)}
													/>
												</div>

												<div className="col-md-3 mt-2">
													<input
														placeholder="Female From"
														type="number"
														className="form-control"
														{...register(
															`normalValues.ageRanges.${index}.femaleFrom`
														)}
													/>
												</div>
												<div className="col-md-3 mt-2">
													<input
														placeholder="Female To"
														type="number"
														className="form-control"
														{...register(
															`normalValues.ageRanges.${index}.femaleTo`
														)}
													/>
												</div>

												<div className="col-md-1 mt-2">
													<button
														type="button"
														className="btn btn-danger"
														onClick={() => removeAge(index)}
													>
														X
													</button>
												</div>
											</div>
										))}

										<button
											type="button"
											className="btn btn-secondary"
											onClick={() =>
												appendAge({
													fromAge: "",
													fromAgeType: "",
													toAge: "",
													toAgeType: "",
													maleFrom: "",
													maleTo: "",
													femaleFrom: "",
													femaleTo: ""
												})
											}
										>
											+ Add Age Range
										</button>
									</div>
								)}
							</div>

							{/* CBC DETAILS */}
							<h6 className="mt-4">CBC Details</h6>
							{fields.map((item, index) => (
								<div className="row" key={item.id}>
									<div className="col-md-3">
										<label>Print Sr No</label>
										<input
											placeholder="Print Sr No"
											type="number"
											className="form-control"
											{...register(`cbcDetails.${index}.printSrNo`)}
										/>
										{errors[`cbcDetails.${index}.printSrNo`] &&
											<p className="text-danger">
												{errors[`cbcDetails.${index}.printSrNo`].message}
											</p>}
									</div>
									<div className="col-md-3">
										<label>Name</label>
										<input
											placeholder="Name"
											className="form-control"
											{...register(`cbcDetails.${index}.name`)}
										/>
										{errors[`cbcDetails.${index}.name`] &&
											<p className="text-danger">
												{errors[`cbcDetails.${index}.name`].message}
											</p>}
									</div>
									<div className="col-md-3">
										<label>Unit</label>
										<input
											placeholder="Unit"
											className="form-control"
											{...register(`cbcDetails.${index}.unit`)}
										/>
										{errors[`cbcDetails.${index}.unit`] &&
											<p className="text-danger">
												{errors[`cbcDetails.${index}.unit`].message}
											</p>}
									</div>
									<div className="col-md-2">
										<label>CBC No</label>
										<input
											placeholder="CBC No"
											type="number"
											className="form-control"
											{...register(`cbcDetails.${index}.cbcNo`)}
										/>
										{errors[`cbcDetails.${index}.cbcNo`] &&
											<p className="text-danger">
												{errors[`cbcDetails.${index}.cbcNo`].message}
											</p>}
									</div>
									<div className="col-md-1 mt-4">
										<button
											type="button"
											className="btn btn-danger"
											onClick={() => remove(index)}
										>
											X
										</button>
									</div>
								</div>
							))}

							<button
								type="button"
								className="btn btn-secondary mt-2"
								onClick={() =>
									append({ printSrNo: "", name: "", unit: "", cbcNo: "" })
								}
							>
								+ Add CBC
							</button>
						</div>

						<div className="card-footer text-end">
							<button className="btn btn-primary" type="submit">
								Submit
							</button>
						</div>
					</form>
				</div>
			</div>
		</Fragment>
	);
};

export default AddPathologyTest;
